import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { BillService } from 'app/core/bill/bill.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { defaultNavigation } from 'app/mock-api/common/navigation/data';
import { cloneDeep } from 'lodash-es';
import { Subject, takeUntil } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    private readonly _defaultNavigation: FuseNavigationItem[] = defaultNavigation;

    // Simulate badge counts for incomplete-product and incomplete-bill
    incompleteProductCount: string = null; // Example dynamic count
    incompleteBillCount: string = null; // Example dynamic count
    private _navigationService = inject(NavigationService);
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(private _fuseMockApiService: FuseMockApiService,
        private _billService: BillService,
        private _userService: UserService
    ) {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: User) => {
                if (user) {
                    this._billService.count$
                        .pipe((takeUntil(this._unsubscribeAll)))
                        .subscribe({
                            next: (response) => {
                                if (response) {
                                    this.incompleteBillCount = response.bill
                                    this.incompleteProductCount = response.product
                                }
                            }
                        })
                }
            });


        this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
            // Clone the default navigation so we can modify it
            const modifiedDefaultNavigation = cloneDeep(this._defaultNavigation);

            // Update badge title for 'product-services'
            const incomplete = this._findNavigationItem(modifiedDefaultNavigation, 'incomplete');
            if (incomplete && incomplete.badge && this.incompleteBillCount && this.incompleteProductCount) {
                incomplete.badge.title = "!";
            }

            // Update badge title for 'incomplete-product'
            const incompleteProductItem = this._findNavigationItem(modifiedDefaultNavigation, 'incomplete-product');

            if (incompleteProductItem && incompleteProductItem.badge) {
                incompleteProductItem.badge.title = this.incompleteProductCount;
            }

            // Update badge title for 'product-services'
            const productServices = this._findNavigationItem(modifiedDefaultNavigation, 'product-services');
            if (productServices && productServices.badge && this.incompleteBillCount && this.incompleteProductCount) {
                productServices.badge.title = "!";
            }

            // Update badge title for 'incomplete-bill'
            const incompleteBillItem = this._findNavigationItem(modifiedDefaultNavigation, 'incomplete-bill');
            if (incompleteBillItem && incompleteBillItem.badge) {
                incompleteBillItem.badge.title = this.incompleteBillCount;
            }

            // Return the response with updated navigation
            return [
                200,
                {
                    default: cloneDeep(modifiedDefaultNavigation),
                },
            ];

        });
    }

    async resetNavigation(): Promise<void> {
        await this.registerHandlers(); // Assuming registerHandlers returns a promise

        // Now call the navigation service to fetch the navigation
        this._navigationService.get().subscribe();
    }


    /**
     * Utility function to find a navigation item by id
     *
     * @param navigation
     * @param id
     */
    private _findNavigationItem(navigation: FuseNavigationItem[], id: string): FuseNavigationItem | null {
        for (const item of navigation) {
            if (item.id === id) {
                return item;
            }

            if (item.children) {
                const childItem = this._findNavigationItem(item.children, id);
                if (childItem) {
                    return childItem;
                }
            }
        }
        return null;
    }
}
